import {
  Box,
  Button,
  IconButton,
  InputLabel,
  TextField,
  Tooltip,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Row, Col, Form } from "react-bootstrap";
import {
  ColDef,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from "ag-grid-enterprise";
import { getExceptionDetailsData } from "../../api/ddExceptionsApi/ddExceptionsApi";
import { GetExceptionsDetails } from "../../types/DocDistroCustomExceptionModel";
import PushPinIcon from "@mui/icons-material/PushPin";
import ExcelIcon from "../../assets/images/ExcelExport.png";
import { UserGridPreferenceModel } from "../../types/UserGridPreferenceModel";
import {
  getUserGridPreference,
  saveUserGridPreference,
} from "../../api/userDataApi/userGridPreferenceAPI";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import "ag-grid-enterprise/styles/ag-grid.css";
import "ag-grid-enterprise/styles/ag-theme-quartz.min.css";

import CustomLoadingOverlay from "../CustomLoadingOverlay/CustomLoadingOverlay ";
import { useSnackbar } from "../Common/SnackbarContext";
import { appSettings } from "../../config/appSetting";
import moment from "moment";
import { FormatDate } from "../Common/CommonMethods";
import LocalStorageUtil from "../../utils/localStorage";

const DDExceptions = () => {
  const pageSizeSelector = appSettings.agGridPageSizeSelector;
  const defaulPageSize = appSettings.agGridDDExceptionDefaulPageSize;
  const { showSnackbar } = useSnackbar();

  const gridRef = useRef<AgGridReact>(null);

  const [rowData, setRowData] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [gridRecordExist, setGridRecordExist] = useState(false);

  //Notification
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searchContent, setsearchContent] = useState("");
  const [_searchContentError, set_SearchContentError] = useState<string>("");//NoSONAR
  const [_showIconButton, set_ShowIconButton] = useState<boolean>(false); //NOSONAR
  const [gridApi, setGridApi] = useState<any>();
  const txtStartDateRef = useRef(startDate);
  const txtEndDateRef = useRef(endDate);
  const txtSearchValueRef = useRef(searchValue);

  //Dynamic grid columns from API response
  const [columnDef, setColumnDef] = useState<ColDef[]>([]);
  const [showLoader, setShowLoader] = useState(false);
  const [savedGridPreferences, setSavedGridPreferences] = useState<string | null>(null);
  const preferencesLoadedRef = useRef(false);
  const preferencesAppliedRef = useRef(false);

  // Fetch data from API
  const getData = async (params: any) => {
    setShowLoader(true);
    try {
      const response = await getExceptionDetailsData(params);
      setRowData(response.getexceptionDetails);

      // Define the desired order of columns
      const columnOrder = [
        "dateTimeStamp",
        "message",
        "stackTrace",
        "serviceName",
        "hostName",
        "fileName",
        "metadataFileName",
        "exceptionStatus",
        "objectValue",
        "resolution",
        "step",
        "type",
        "createDate",
      ];

      if (response.getexceptionDetails.length > 0) {
        const firstRow = response.getexceptionDetails[0];
        const allKeys = Object.keys(firstRow);

        // Create column definitions for desired order
        const desiredColumnDefs: ColDef[] = columnOrder.map((key) => ({
          // NOSONAR
          headerName: toPascalCase(key),
          field: key === "createDate" ? "dateTimeStamp" : key,
          filter: true,
          autoHeight: true,
          valueFormatter:
            key === "dateTimeStamp"
              ? (params: any) => FormatDate(params.value)
              : undefined,
        }));

        // Create column definitions for remaining columns
        const remainingColumnDefs: ColDef[] = allKeys
          .filter((key) => !columnOrder.includes(key)) // NOSONAR
          .map((key) => ({
            // NOSONAR
            headerName: toPascalCase(key),
            field: key,
            filter: true,
            autoHeight: true,
            enableRowGroup: true,
            valueFormatter:
              key === "dateTimeStamp"
                ? (params: any) => FormatDate(params.value)
                : undefined,
          }));

        // Combine desired and remaining columns
        const newColumnDefs: ColDef[] = [
          ...desiredColumnDefs.map((col) => ({
            ...col,
            valueFormatter:
              col.field === "stackTrace" ? formatStackTrace : undefined,
            filterParams:
              col.field === "stackTrace"
                ? { valueFormatter: formatStackTrace }
                : undefined,
          })),
          ...remainingColumnDefs.map((col) => ({
            ...col,
          })),
        ];

        setColumnDef(newColumnDefs);
      } else {
        setColumnDef([]);
      }
      setGridRecordExist(response.getexceptionDetails.length > 0);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setShowLoader(false);
    }
  };

  function formatStackTrace(params: { value: string }) {
    if (!params.value) return "";
    try {
      return JSON.stringify(JSON.parse(params.value), null, 2);
    } catch (error) {
      return params.value;
    }
  }
  // Helper function to convert a string to PascalCase
  function toPascalCase(str: string): string {
    return str
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  const getUserGridPreferences = async () => {
    setShowLoader(true);
    const userGridPreference = {
      Area: "DDExceptions",
      UserID: Number(LocalStorageUtil.getItem("userId")),
      BusinessUnitID: Number(LocalStorageUtil.getItem("businessUnitId")),
      ContextName: LocalStorageUtil.getItem("contextName")?.toString(),
    };

    try {
      const docDistroCustomExceptionModel = await getUserGridPreference(
        userGridPreference
      );
      if (docDistroCustomExceptionModel) {
        showSnackbar("Grid state apply successfully.");
      }
      return docDistroCustomExceptionModel;
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setShowLoader(false);
    }
  };

  //Apply grid preferences
  const applyGridPreferences = (gridPreferences: any) => {
    if (gridPreferences) {
      try {
        const gridState = JSON.parse(gridPreferences);
        if (gridRef.current) {
          gridRef.current.api.applyColumnState({
            state: gridState,
            applyOrder: true,
          });
        }
      } catch (error) {
        console.error("Error parsing grid preferences:", error);
      }
    } else {
      console.warn("No grid preferences found or GridBlob is undefined.");
    }
  };

  // Export data to excel
  const handleExport = () => {
    if (gridRef.current) {
      const predefinedColumns = [
        "rowIndex",
        "dateTimeStamp",
        "exceptionStatus",
        "fileName",
        "metadataFileName",
        "message",
        "serviceName",
        "stackTrace",
        "objectValue",
        "hostName",
        "resolution",
        "step",
        "type",
      ]; // Define the columns to be exported
      gridRef.current.api.exportDataAsExcel({
        fileName:
          "DDExceptions Reports_" + moment().format("DD-MM-YYYY") + ".xlsx",
        sheetName: "DDExceptions",
        columnKeys: predefinedColumns,
        processCellCallback: function (params) {
          return params.value;
        },
      });
    }
  };

  useEffect(() => {
    txtStartDateRef.current = startDate;
    txtEndDateRef.current = endDate;
    txtSearchValueRef.current = searchContent;
  }, [startDate, endDate, searchContent]);

  // Load grid preferences once on mount
  useEffect(() => {
    const loadGridPreferences = async () => {
      if (!preferencesLoadedRef.current) {
        const gridPreferences = await getUserGridPreferences();
        if (gridPreferences) {
          setSavedGridPreferences(gridPreferences);
        }
        preferencesLoadedRef.current = true;
      }
    };
    loadGridPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply preferences when loaded and grid is ready - ONLY ONCE
  useEffect(() => {
    if (savedGridPreferences && gridApi && columnDef.length > 0 && !preferencesAppliedRef.current) {
      setTimeout(() => {
        applyGridPreferences(savedGridPreferences);
        preferencesAppliedRef.current = true;
      }, 300);
    }
  }, [savedGridPreferences, gridApi, columnDef]);

  //Save grid preferences
  const handleSaveGridPreferences = async () => {
    setShowLoader(true);
    if (gridRef.current) {
      const gridState = gridRef.current.api.getColumnState();
      const userGridPreference: UserGridPreferenceModel = {
        Area: "DDExceptions",
        UserID: Number(LocalStorageUtil.getItem("userId")),
        BusinessUnitID: Number(LocalStorageUtil.getItem("businessUnitId")),
        ContextName: LocalStorageUtil.getItem("contextName")?.toString(),
        GridBlob: JSON.stringify(gridState),
        LastModifiedBy: "DocDistro-UserGridPreferencesAPI",
        LastModifiedDate: new Date().toISOString(),
      };

      try {
        const docDistroCustomExceptionModel = await saveUserGridPreference(
          userGridPreference
        );
        if (docDistroCustomExceptionModel) {
          showSnackbar("Grid state successfully saved.");
          // Update saved preferences state
          setSavedGridPreferences(userGridPreference.GridBlob ?? null);
        }
      } catch (error) {
        console.error("Error fetching data", error);
      } finally {
        setShowLoader(false);
      }
    }
  };

  const clearSelectionState = useCallback(() => {
    if (gridRef.current) {
      gridRef.current.api.setServerSideSelectionState({
        selectAll: false,
        toggledNodes: [],
      });
    }
  }, []);

  // Apply button click event
  const handleApplyButton = useCallback(
    async (e?: React.FormEvent) => {
      if (startDate && endDate && startDate > endDate) {
        showSnackbar("From date should be less than To date", true);
      } else {
        setTotalRecordsCount(0); // Reset total records count
        const params: any = {
          Fromdate: formatDate(startDate),
          Todate: formatDate(endDate),
          NoOfRecords: cacheBlockSize,
          SortColumn: "",
          SortOrder: 0,
          TotalRecords: 0,
          IsExportToExcel: true,
          Timezone: appSettings.timezone,
          ContextName:
            LocalStorageUtil.getItem("contextName")?.toString() ?? "",
          Pagename: null,
          SearchTerm: null,
          IsFromDownStream: false,
          CurrentPage: 1,
          GetExceptionDetails: null,
        };
        getData(params);

        if (gridApi) {
          clearSelectionState();
          gridApi.onFilterChanged();
        }
      }
    },
    [gridApi, clearSelectionState, startDate, endDate]
  );

  //handel reset button
  const handleReset = useCallback(
    async (e?: React.FormEvent) => {
      setStartDate(null);
      setEndDate(null);
      setSearchValue("");
      setTotalRecordsCount(0); // Reset total records count
      const params: any = {
        Fromdate: null,
        Todate: null,
        NoOfRecords: cacheBlockSize,
        SortColumn: "",
        SortOrder: 0,
        TotalRecords: 0,
        IsExportToExcel: true,
        Timezone: appSettings.timezone,
        ContextName: LocalStorageUtil.getItem("contextName")?.toString() ?? "",
        Pagename: null,
        SearchTerm: null,
        IsFromDownStream: false,
        CurrentPage: 1,
        GetExceptionDetails: null,
      };
      // Fetch data only after the condition is met
      getData(params);

      if (gridApi) {
        clearSelectionState();
        gridApi.onFilterChanged();
      }
    },
    [gridApi, clearSelectionState]
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setsearchContent(event.target.value);
  };

  // Search content on enter key press
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };
  // Handel search button click
  const handleSearchSubmit = useCallback(
    async (e?: React.FormEvent) => {
      try {
        const today = new Date();
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(today.getDate() - 7);

        if (searchContent.trim().length > 0) {
          set_SearchContentError("");
          setTotalRecordsCount(0); // Reset total records count
          const params: any = {
            Fromdate: formatDate(startDate) ?? null,
            Todate: formatDate(endDate) ?? null,
            NoOfRecords: cacheBlockSize,
            SortColumn: "",
            SortOrder: 0,
            TotalRecords: 0,
            IsExportToExcel: true,
            Timezone: appSettings.timezone,
            ContextName:
              LocalStorageUtil.getItem("contextName")?.toString() ?? "",
            Pagename: null,
            SearchTerm: searchContent,
            IsFromDownStream: false,
            CurrentPage: 1,
            GetExceptionDetails: null,
          };
          getData(params);
        } else {
          set_SearchContentError("Please enter search content");
        }
        if (gridApi) {
          clearSelectionState();
          gridApi.onFilterChanged();
        }
      } catch (error) {
        console.log(error);
      }
    },
    [searchContent, gridApi, clearSelectionState, startDate, endDate]
  );

  // Handel search content clear
  const handelSearchContentClear = () => {
    setsearchContent("");
    set_SearchContentError("");
    set_ShowIconButton(false);
    setTotalRecordsCount(0); // Reset total records count
    try {
      const params: any = {
        Fromdate: formatDate(startDate) ?? null,
        Todate: formatDate(endDate) ?? null,
        NoOfRecords: cacheBlockSize,
        SortColumn: "",
        SortOrder: 0,
        TotalRecords: 0,
        IsExportToExcel: true,
        Timezone: appSettings.timezone,
        ContextName: LocalStorageUtil.getItem("contextName")?.toString() ?? "",
        Pagename: null,
        SearchTerm: "",
        IsFromDownStream: false,
        CurrentPage: 1,
        GetExceptionDetails: null,
      };
      getData(params);

      if (gridApi) {
        clearSelectionState();
        gridApi.onFilterChanged();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: appSettings.timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const formatDate = (date: any) => {
    if (!date) return null;
    const parts = formatter.formatToParts(date).reduce((acc: any, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});
    return `${parts.month}-${parts.day}-${parts.year}`;
  };

  const [cacheBlockSize, setcacheBlockSize] = useState(10);
  const [totalRecordsCount, setTotalRecordsCount] = useState(0);

  // Handle when user removes all row groups
  const onColumnRowGroupChanged = useCallback(async () => {
    if (gridRef.current?.api) {
      const columnState = gridRef.current.api.getColumnState();
      const hasRowGroups = columnState.some(col => col.rowGroup === true);
    }
  }, []);

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);

    const dataSource: IServerSideDatasource = {
      getRows: function (params: IServerSideGetRowsParams) {
        (async () => {
          const currentPage = params.api.paginationGetCurrentPage() + 1;
          const pageSize = params.api.paginationGetPageSize();
          const { sortModel } = params.request;
          const startRow = params.request.startRow ?? 0;

          // Check if we've already reached the end of data
          if (totalRecordsCount > 0 && startRow >= totalRecordsCount) {
            params.success({
              rowData: [],
              rowCount: totalRecordsCount,
            });
            return;
          }

          let sortOrder = 0;
          let sortColumn = null;

          if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0].sort;
            const colId = sortModel[0].colId.replace(/_\d+$/, "");
            sortOrder = sort === "asc" ? 0 : 1;
            sortColumn = colId;
          }

          setcacheBlockSize(pageSize);

          try {
            const payload: GetExceptionsDetails = {
              Fromdate: txtStartDateRef.current,
              Todate: txtEndDateRef.current,
              NoOfRecords: pageSize,
              SortColumn: sortColumn ?? "",
              SortOrder: sortOrder,
              TotalRecords: 0,
              IsExportToExcel: true,
              Timezone: appSettings.timezone,
              ContextName:
                LocalStorageUtil.getItem("contextName")?.toString() ?? "",
              Pagename: null,
              SearchTerm: txtSearchValueRef.current,
              IsFromDownStream: false,
              CurrentPage: currentPage,
              GetExceptionDetails: null,
            };

            const response = await getExceptionDetailsData(payload);
            setRowData(response.getexceptionDetails);
            setTotalRecordsCount(response.totalRecords);

            // Define the desired order of columns
            const columnOrder = [
              "dateTimeStamp",
              "message",
              "stackTrace",
              "serviceName",
              "hostName",
              "fileName",
              "metadataFileName",
              "exceptionStatus",
              "objectValue",
              "resolution",
              "step",
              "type",
              "createDate",
            ];

            if (response.getexceptionDetails.length > 0) {
              const firstRow = response.getexceptionDetails[0];
              const allKeys = Object.keys(firstRow);

              // Create column definitions for desired order
              const desiredColumnDefs: ColDef[] = columnOrder.map((key) => ({
                // NOSONAR
                headerName: toPascalCase(key),
                field: key === "createDate" ? "dateTimeStamp" : key,
                filter: true,
                autoHeight: true,
                enableRowGroup: true,
                valueFormatter:
                  key === "dateTimeStamp"
                    ? (params: any) => FormatDate(params.value)
                    : undefined,
              }));

              // Create column definitions for remaining columns
              const remainingColumnDefs: ColDef[] = allKeys
                .filter((key) => !columnOrder.includes(key)) // NOSONAR
                .map((key) => ({
                  // NOSONAR
                  headerName: toPascalCase(key),
                  field: key,
                  filter: true,
                  autoHeight: true,
                  enableRowGroup: true,
                  valueFormatter:
                    key === "dateTimeStamp"
                      ? (params: any) => FormatDate(params.value)
                      : undefined,
                }));

              // Combine desired and remaining columns
              const newColumnDefs: ColDef[] = [
                ...desiredColumnDefs.map((col) => {
                  let valueFormatter;
                  if (col.field === "stackTrace") {
                    valueFormatter = formatStackTrace;
                  } else if (col.field === "dateTimeStamp") {
                    valueFormatter = (params: any) => FormatDate(params.value);
                  } else {
                    valueFormatter = col.valueFormatter;
                  }
                  return {
                    ...col,
                    valueFormatter,
                    filterParams:
                      col.field === "stackTrace"
                        ? { valueFormatter: formatStackTrace }
                        : undefined,
                  };
                }),
                ...remainingColumnDefs.map((col) => ({
                  ...col,
                })),
              ];

              setColumnDef(newColumnDefs);

              setTimeout(() => { // NOSONAR
                // Check if this is the last page or if we have less data than requested
                const isLastPage =
                  response.getexceptionDetails.length < pageSize ||
                  startRow + response.getexceptionDetails.length >=
                    response.totalRecords;

                params.success({
                  rowData: response.getexceptionDetails,
                  rowCount: isLastPage
                    ? startRow + response.getexceptionDetails.length
                    : response.totalRecords,
                });
              }, 300);
            } else {
              setTimeout(() => {// NOSONAR
                params.success({
                  rowData: [],
                  rowCount: 0,
                });
                params.api.showNoRowsOverlay();
              }, 1000);
            }
            setGridRecordExist(response.getexceptionDetails.length > 0);
          } catch (error) {
            params.success({
              rowData: [],
              rowCount: 0,
            });
          } finally {
            setShowLoader(false);
          }
        })();
      },
    };
    params.api.setGridOption("serverSideDatasource", dataSource);
  };

  // Render the component
  return (
    <div style={{ overflow: "hidden" }}>
      <NavigationBar title="DDExceptions" text="DDExceptions" />
      <Row style={{ margin: "1em" }}>
        <Col>
          <Box>
            <div className="rp-main-inner col-sm-12">
              <div className="queue-top row clearfix" style={{ width: "100%" }}>
                <div
                  className="col-sm-12"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: ".5em",
                  }}
                >
                  {/* Dates, Apply and Reset buttons */}
                  <div
                    className="col-sm-11"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: ".5em",
                    }}
                  >
                    <InputLabel
                      style={{
                        justifyContent: "center",
                        alignItems: "center",
                        color: "var(--text-color)",
                        display: "inline-block !important",
                        marginBottom: "5px !important",
                        fontWeight: 600,
                        fontSize: "11px",
                      }}
                    >
                      From Date: *
                    </InputLabel>
                    <Form.Control
                      type="date"
                      placeholder="From Date"
                      name="fromdate"
                      max={new Date().toISOString().split("T")[0]}
                      value={
                        startDate ? startDate.toISOString().split("T")[0] : ""
                      }
                      onChange={(e) => setStartDate(new Date(e.target.value))}
                      style={{
                        padding: ".3em",
                        width: "15%",
                        fontSize: "11px",
                        borderRadius: "0",
                        border: "1px solid #ccc",
                        borderColor: "black",
                      }}
                    />
                    <InputLabel
                      style={{
                        justifyContent: "center",
                        alignItems: "center",
                        color: "var(--text-color)",
                        display: "inline-block !important",
                        marginBottom: "5px !important",
                        fontWeight: 600,
                        fontSize: "11px",
                      }}
                    >
                      To Date: *
                    </InputLabel>
                    <Form.Control
                      type="date"
                      placeholder="To Date"
                      name="todate"
                      value={endDate ? endDate.toISOString().split("T")[0] : ""}
                      max={new Date().toISOString().split("T")[0]}
                      onChange={(e) => setEndDate(new Date(e.target.value))}
                      style={{
                        padding: ".3em",
                        width: "15%",
                        fontSize: "11px",
                        borderRadius: "0",
                        border: "1px solid #ccc",
                        borderColor: "black",
                      }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleApplyButton}
                      className="button apply-button"
                      data-testid="apply-button"
                    >
                      Apply
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleReset}
                      className="button reset-button"
                      data-testid="reset-apply-button"
                    >
                      Reset
                    </Button>
                  </div>

                  {/* Export to excel */}
                  <div
                    className="col-sm-1"
                    style={{
                      marginLeft: ".1em",
                      justifyContent: "right",
                      display: "flex",
                      alignItems: "center",
                      gap: ".5em",
                    }}
                  >
                    {gridRecordExist && (
                      <>
                        <Tooltip title="Click to save grid state">
                          <IconButton
                            onClick={handleSaveGridPreferences}
                            className="preference-icon"
                            data-testid="preference-icon"
                          >
                            <PushPinIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Click to export the grid content in excel">
                          <button
                            type="button"
                            onClick={handleExport}
                            style={{
                              background: "none",
                              border: "none",
                              padding: 0,
                              cursor: "pointer",
                            }}
                            aria-label="Export to Excel"
                          >
                            <img
                              src={ExcelIcon}
                              alt="Doc-Distro"
                              className="excel-icon"
                              data-testid="excel-export"
                            />
                          </button>
                        </Tooltip>
                      </>
                    )}
                  </div>
                </div>
                <div
                  className="col-sm-12"
                  style={{
                    display: "flex",
                    gap: ".5em",
                    alignItems: "center",
                    marginTop: "1em",
                    marginLeft: "4.5em",
                  }}
                >
                  <div
                    className="col-sm-8"
                    style={{
                      display: "flex",
                      gap: "10px",
                      justifyContent: "left",
                      alignItems: "left",
                    }}
                  >
                    <TextField
                      id="txtDDExceptions"
                      variant="outlined"
                      fullWidth
                      style={{
                        margin: "0em 0em 1em -.5em",
                        width: "37.5em",
                        fontSize: "11px",
                        borderRadius: "0",
                      }}
                      placeholder="Search"
                      type="text"
                      name="search"
                      value={searchContent}
                      onChange={handleInputChange}
                      onKeyDown={handleSearchKeyPress}
                      className="input-sm form-control col-sm-3"
                      required
                      InputProps={{
                        style: {
                          backgroundColor: "white",
                          height: "25px",
                          fontSize: "11px",
                        },
                      }}
                    ></TextField>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSearchSubmit}
                      className="button search-button"
                      data-testid="search-button"
                    >
                      Search
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handelSearchContentClear}
                      className="button reset-button"
                      data-testid="reset-search-button"
                    >
                      Reset
                    </Button>
                  </div>
                </div>
                {/* AgGrid */}
                <div className="rp-main-inner col-sm-12">
                  <div className="queue-table-view clearfix">
                    <div
                      className="ag-theme-quartz-dark"
                      style={{
                        height: "60vh",
                        width: "102%",
                        marginTop: "1vh",
                      }}
                    >
                      <AgGridReact
                        columnDefs={columnDef}
                        rowData={rowData}
                        ref={gridRef}
                        pagination={true}
                        paginationPageSize={defaulPageSize}
                        paginationPageSizeSelector={pageSizeSelector}
                        headerHeight={30}
                        rowGroupPanelShow="always"
                        pivotPanelShow="always"
                        rowModelType="serverSide"
                        onGridReady={onGridReady}
                        onColumnRowGroupChanged={onColumnRowGroupChanged}
                        cacheBlockSize={cacheBlockSize}
                        overlayNoRowsTemplate={
                          '<span class="ag-overlay-loading-center">No rows to show</span>'
                        }
                        loading={!rowData || showLoader}
                        loadingOverlayComponent={CustomLoadingOverlay}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Box>
        </Col>
      </Row>
    </div>
  );
};

export default DDExceptions;
